import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import auth from '../../../../services/authService';
import songService from '../../../../services/songService';
import { scrollTop } from '../../../../utils/utilities';
import { useGlobalContext } from '../../../../context/GlobalContext';
import UpgradeModal from '../../../../components/subscription/UpgradeModal';
import Icon from '../../../../components/common/Icon';
import LoadingDots from '../../../../components/common/LoadingDots';
import { ReactComponent as TeamIcon } from '../../../../icons/group.svg';
import { ReactComponent as PlusIcon } from '../../../../icons/plus.svg';
import {
  canAddPublicSongToday,
  markPublicSongAdded,
  hasReachedLimit,
  SUBSCRIPTION_ENABLED,
} from '../../../../utils/subscription';

import './PublicSongsResult.scss';

const SearchSongsResult = ({ songs }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = auth.getCurrentUser();
  const { songs: teamSongs, setSongs, dataLoaded } = useGlobalContext();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeNotice, setUpgradeNotice] = useState(null);
  const [addingSongId, setAddingSongId] = useState(null);

  const mapOwnSong = (song) => {
    if (song.team._id === auth.getCurrentUser().teamId) {
      song.ownSong = true;
      return song;
    } else {
      return song;
    }
  };
  // Add a ownSong property set to true when the song belongs to the team searching songs
  const updatedSongs = songs.map(mapOwnSong);

  const handleAddSong = async (songId) => {
    if (SUBSCRIPTION_ENABLED && hasReachedLimit(teamSongs)) {
      setShowUpgradeModal(true);
      return;
    }

    if (SUBSCRIPTION_ENABLED && !canAddPublicSongToday()) {
      setUpgradeNotice(t('publicSongs.dailyLimit'));
      setShowUpgradeModal(true);
      return;
    }

    setAddingSongId(songId);
    try {
      const songFromDb = await songService.getSong(songId);
      const formattedSong = formatCopySong(songFromDb);

      const response = await songService.saveSong(formattedSong);
      const savedSong = response.data;

      markPublicSongAdded();

      if (dataLoaded && savedSong) {
        setSongs([savedSong, ...teamSongs]);
      }

      if (window.innerWidth < 992) scrollTop(null, false);
      history.push('/songs', { highlightedSongId: savedSong._id });
    } catch (ex) {
      setAddingSongId(null);
    }
  };

  return (
    <div className="PublicSongsResult">
      <ul className="songs">
        {updatedSongs.map((song) => {
          const isAdding = addingSongId === song._id;

          return (
            <li key={song._id} className="song">
              <div className="info">
                <p className="title">
                  <Link to={`/view/${song._id}`}>{song.title}</Link>
                </p>
                <p className="artist">{song.artist}</p>
                <div className="chips">
                  <span className="chip">{song.key}</span>
                  <span className="chip">
                    <Icon icon={<TeamIcon />} />
                    {song.team.name}
                  </span>
                </div>
              </div>
              {!song.ownSong && user && user.isAdmin && (
                <button
                  onClick={() => handleAddSong(song._id)}
                  type="button"
                  disabled={isAdding}
                  className="add"
                  aria-label={t('publicSongs.addTo', { title: song.title })}
                >
                  <span className={cx('label', { 'is-hidden': isAdding })}>
                    <Icon icon={<PlusIcon />} />
                    {t('publicSongs.add')}
                  </span>
                  {isAdding && <LoadingDots className="adding-dots" />}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {SUBSCRIPTION_ENABLED && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          notice={upgradeNotice}
          onClose={() => {
            setShowUpgradeModal(false);
            setUpgradeNotice(null);
          }}
          onUpgrade={() => {
            setShowUpgradeModal(false);
            setUpgradeNotice(null);
            history.push('/subscribe');
          }}
        />
      )}
    </div>
  );
};

// Formats the song as a new song to be saved, but by another team instead of the original one
const formatCopySong = (song) => {
  const songCopy = { ...song };

  songCopy.isPublic = false;
  songCopy.categoryId = songCopy.category._id;
  songCopy.teamId = auth.getCurrentUser().teamId;

  // Strip hidden/note parts and user assignments
  // Hidden parts are filtered out by the backend automatically, but filtering here just in case.
  songCopy.parts = songCopy.parts
    .filter((part) => !['Hidden', 'Note'].includes(part.name))
    .map(({ assignedUsers, ...rest }) => rest);

  if (!songCopy.bpm) delete songCopy.bpm; // delete instead of setting to empty to prevent validation errors
  if (!songCopy.youtube) songCopy.youtube = '';

  // isHidden is required by the schema but may be absent on older songs
  if (songCopy.isHidden === undefined) songCopy.isHidden = false;

  // New properties
  songCopy.isCopy = true;
  songCopy.originalTeam = { _id: songCopy.team._id, name: songCopy.team.name };

  if (songCopy.stats) songCopy.stats = { ...songCopy.stats, timesPlayed: 0 };

  // Remove properties
  delete songCopy._id;
  delete songCopy.__v;
  delete songCopy.category;
  delete songCopy.team;
  delete songCopy.createdAt;
  delete songCopy.updatedAt;
  delete songCopy.lastServicePlay;

  if (typeof songCopy.transposeBy !== 'number' || songCopy.transposeBy === 0) {
    delete songCopy.transposeBy;
  }

  return songCopy;
};

export default SearchSongsResult;
